import React, { useCallback, useState } from 'react';
import { BatchOrdersResult } from 'models/order';
import BatchProductionFinishedDialog from 'components/orders/shared/batch-production-finished-dialog.component';
import BatchSendToClinicDialog from 'components/orders/shared/batch-send-to-clinic-dialog.component';
import ExternalUserBatchSendToClinicDialog from 'components/orders/shared/external-user-batch-send-to-clinic-dialog.component';
import BatchOrdersResultSnackbar from 'components/orders/shared/batch-orders-result-snackbar.component';
import DeliveryNoteBatchSnackbar from 'components/orders/shared/delivery-note-batch-snackbar.component';
import { useDeliveryNoteBatch } from 'components/orders/shared/use-delivery-note-batch';
import {
    BATCH_PRODUCTION_FINISHED_ACTION,
    BATCH_SEND_TO_CLINIC_ACTION,
    isBatchOrderActionType,
} from 'components/orders/shared/batch-order-actions';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

type BatchOrderActionsOptions = {
    // Called once a batch has run, with what it did; the page refreshes its list here.
    onCompleted: (result: BatchOrdersResult, actionType: string) => void;
};

/**
 * DD-3463: the dialogs, result message and compiled-PDF snackbar behind the two
 * order-state batch actions, so the three lists wire them the same way.
 */
export const useBatchOrderActions = ({ onCompleted }: BatchOrderActionsOptions) => {
    const currentUser = currentUserVar();
    const isElevatedExternalUser = permissions.isElevatedExternalUser(currentUser.role);
    const [openAction, setOpenAction] = useState<string | null>(null);
    const [orderIds, setOrderIds] = useState<string[]>([]);
    const [result, setResult] = useState<BatchOrdersResult | null>(null);
    const deliveryNoteBatch = useDeliveryNoteBatch();

    const openBatchOrderAction = useCallback((type: string, selectedOrderIds: string[]): boolean => {
        if (!isBatchOrderActionType(type)) {
            return false;
        }
        setOrderIds(selectedOrderIds);
        setOpenAction(type);
        return true;
    }, []);

    const close = () => setOpenAction(null);

    const handleDone = (actionType: string) => (batchResult: BatchOrdersResult) => {
        setOpenAction(null);
        setResult(batchResult);
        if (batchResult.deliveryNoteBatchId) {
            deliveryNoteBatch.start(batchResult.deliveryNoteBatchId, batchResult.succeeded.length);
        }
        onCompleted(batchResult, actionType);
    };

    const dialogs = (
        <>
            {openAction === BATCH_PRODUCTION_FINISHED_ACTION ? (
                <BatchProductionFinishedDialog
                    open
                    orderIds={orderIds}
                    onClose={close}
                    onDone={handleDone(BATCH_PRODUCTION_FINISHED_ACTION)}
                />
            ) : undefined}
            {openAction === BATCH_SEND_TO_CLINIC_ACTION ? (
                // DD-3391: the elevated external tier gets its own dialog and mutation
                // rather than a version of the lab's with sections switched off.
                isElevatedExternalUser ? (
                    <ExternalUserBatchSendToClinicDialog
                        open
                        orderIds={orderIds}
                        onClose={close}
                        onDone={handleDone(BATCH_SEND_TO_CLINIC_ACTION)}
                    />
                ) : (
                    <BatchSendToClinicDialog
                        open
                        orderIds={orderIds}
                        onClose={close}
                        onDone={handleDone(BATCH_SEND_TO_CLINIC_ACTION)}
                    />
                )
            ) : undefined}
            <BatchOrdersResultSnackbar result={result} onClose={() => setResult(null)} />
            <DeliveryNoteBatchSnackbar state={deliveryNoteBatch.state} onDismiss={deliveryNoteBatch.dismiss} />
        </>
    );

    return { openBatchOrderAction, dialogs };
};
